/**
 * PATH: packages/email-hub/src/feature/hooks/useEmails.sync.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAILS-SYNC-001
 * PURPOSE: Process-wide single-flight email-sync subsystem — the module-scope
 *          mutex/state (g_sync*), its pub-sub mutators, doSync (the timed,
 *          dedup'd POST /api/emails/sync/ driver), the useSyncEmails hook, and
 *          the EMAIL_SYNC_QUEUED / EMAIL_SYNC_AUTH_FAILED event channels.
 * WHY: Extracted from the useEmails god-hook (ARC EMAIL_HUB_LOADING_RESILIENCE
 *      phase 3 step 5, expand->migrate->contract). This is the one genuinely
 *      STATEFUL subsystem: the mutex + result/error caches + the isSyncing flag
 *      MUST stay module-scoped (exactly one sync in flight per tab regardless of
 *      how many components call useSyncEmails). Keeping ALL the mutable state and
 *      every reader/writer together in this single module preserves the
 *      single-shared-instance semantics — nothing reassigns these bindings
 *      across a module boundary. Zero behaviour change.
 * RELATED:
 *   - ./useEmails.ts: re-exports the public surface; its inbox/stats hooks
 *     listen on EMAIL_SYNC_QUEUED_EVENT.
 *   - ./useEmails.types.ts: SyncResult shape.
 *   - ../lib/api: emailApi transport.
 *   - useEmailBackfill.ts: brackets long chunk polls via setSyncQueuedWaiting.
 *   - useEmails.invalidate.test.ts: pins the event-name surface (source reader
 *     concats this file).
 */
import { useCallback, useEffect, useState } from 'react'
import { emailApi } from '../lib/api'
import { QuotechApiError } from '../../_internal/services/quotechApi'
import type { SyncEndpointResponse, SyncResult } from './useEmails.types'

/**
 * Custom event fired when a sync attempt fails because the user's
 * authentication is no longer valid (JWT expired, Outlook token type
 * rejected, etc). The EmailLayout listens for it to suspend the
 * auto-sync timer and the backfill loop until the next page reload
 * or user action — otherwise the timers spam the same 401 forever.
 */
export const EMAIL_SYNC_AUTH_FAILED_EVENT = 'email-sync-auth-failed'

/** Heuristic — match anything that smells like an auth/token rejection. */
function looksLikeAuthFailure(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  const m = err.message.toLowerCase()
  return (
    m.includes('not valid for any token type') ||
    m.includes('token_not_valid') ||
    m.includes('authentication') ||
    m.includes('unauthorized') ||
    m.includes(' 401') ||
    m.endsWith('401') ||
    m.startsWith('401')
  )
}

/**
 * Sync hook with a process-wide single-flight guarantee.
 *
 * The mutex MUST be module-scoped, not per-hook-instance. Three
 * components call `useSyncEmails()` independently — `EmailLayout`
 * (manual button + auto-sync timer + backfill loop), `InboxPage`
 * (its own header sync button), and `SyncSettingsPage`. A per-hook
 * ref didn't dedupe across those instances, so any two could still
 * race and the loser got the backend's "Sync already in progress"
 * 409. Hoisting the in-flight slot, the result/error caches, and
 * the `isSyncing` flag to module scope (with a tiny pub-sub for
 * subscribers) means there's exactly one sync in flight per tab no
 * matter how many components ask for it.
 *
 * The 409 from the *backend* (a different tab racing, or a stale
 * server-side lock) is also swallowed here as a no-op rather than
 * re-thrown — otherwise the backfill / auto-sync loops would record
 * spurious `backfill_chunk_failed` rows in the integration log every
 * time another window beat us to the punch.
 */
let g_syncInFlight: Promise<SyncResult> | null = null
let g_syncInFlightForceFull = false
let g_syncIsSyncing = false
let g_syncLastResult: SyncResult | null = null
let g_syncLastError: string | null = null
const g_syncSubscribers = new Set<() => void>()

// Reference-counted "a queued backfill chunk is still being resolved
// in the background" flag. The Quotech backend returns 202 with a
// `task_id` for sync requests and the actual ingestion happens on a
// Celery worker that can take 30-60s for a wide date-range chunk.
// Without this counter the public `isSyncing` flag drops to false
// the moment the POST returns (a second or two), so the Sync button
// flickers Syncing → Sync → Syncing → Sync as the backfill walker
// fires successive chunks. Callers that own the queued-resolve loop
// (currently only useEmailBackfill) bracket their long poll with
// `setSyncQueuedWaiting(true)` / `(false)` so the button reflects
// the full chunk lifecycle, not just the in-flight HTTP request.
let g_syncQueuedWaitingCount = 0
// Settle debounce: when `setSyncIsSyncing(false)` fires, we delay
// the actual notification by SETTLE_MS so a back-to-back next chunk
// (or a queued-waiting bracket arriving microseconds later) absorbs
// the transition instead of producing a one-frame flash of "Sync"
// in the button. SETTLE_MS is small enough not to keep stale state
// visible when the user truly stops syncing.
const SYNC_SETTLE_MS = 1_500
let g_syncIsSyncingSettleTimer: number | null = null

function notifySyncSubscribers(): void {
  g_syncSubscribers.forEach(fn => {
    try { fn() } catch { /* ignore */ }
  })
}

function effectiveIsSyncing(): boolean {
  return g_syncIsSyncing || g_syncQueuedWaitingCount > 0
}

function setSyncIsSyncing(v: boolean): void {
  if (g_syncIsSyncing === v) return
  if (v) {
    // Cancel any pending settle — we're back to actively syncing.
    if (g_syncIsSyncingSettleTimer !== null) {
      window.clearTimeout(g_syncIsSyncingSettleTimer)
      g_syncIsSyncingSettleTimer = null
    }
    g_syncIsSyncing = true
    notifySyncSubscribers()
    return
  }
  // Defer the false-flip; if a new sync starts within SETTLE_MS the
  // button stays "Syncing" continuously instead of strobing.
  if (g_syncIsSyncingSettleTimer !== null) {
    window.clearTimeout(g_syncIsSyncingSettleTimer)
  }
  g_syncIsSyncingSettleTimer = window.setTimeout(() => {
    g_syncIsSyncingSettleTimer = null
    if (g_syncIsSyncing) {
      g_syncIsSyncing = false
      notifySyncSubscribers()
    }
  }, SYNC_SETTLE_MS)
}

/** Reference-counted bracket for "a queued sync chunk is still being
 *  resolved in the background". Must be paired (true → false). Used
 *  by `useEmailBackfill` to keep the public `isSyncing` flag true
 *  across the entire chunk lifecycle (POST → queued → poll → done)
 *  rather than just the brief HTTP request portion. Safe to call
 *  from anywhere; the counter handles concurrent overlap. */
export function setSyncQueuedWaiting(active: boolean): void {
  const prev = effectiveIsSyncing()
  if (active) {
    g_syncQueuedWaitingCount += 1
  } else {
    g_syncQueuedWaitingCount = Math.max(0, g_syncQueuedWaitingCount - 1)
  }
  const next = effectiveIsSyncing()
  if (prev !== next) notifySyncSubscribers()
}

function setSyncLastResult(r: SyncResult | null): void {
  g_syncLastResult = r
  notifySyncSubscribers()
}

function setSyncLastError(e: string | null): void {
  if (g_syncLastError === e) return
  g_syncLastError = e
  notifySyncSubscribers()
}

function looksLikeAlreadyInProgress(msg: string): boolean {
  const m = msg.toLowerCase()
  return m.includes('already in progress') || m.includes('please wait')
}

/** Conservative client-side clamp for *delta-mode* (non-force_full)
 *  syncs. The Quotech-backend `EmailSyncInputSerializer` accepts
 *  `days_back` up to 3650, but a non-force sync uses the delta cursor
 *  and the days_back hint is largely ignored anyway — capping at 30
 *  keeps the auto-sync request payload bounded. force_full=true
 *  lifts this clamp to FORCE_FULL_DAYS_BACK_MAX (10 years, matching
 *  the backend serializer). */
const SERVER_DAYS_BACK_MAX = 30
/** Backend `EmailSyncInputSerializer` v3 cap. Lift the wider client
 *  clamp to match — the dashboard's progressive backfill walker
 *  fires `force_full=true` chunks all the way out to 10 years to
 *  silently exhaust the user's mailbox retention. Any value over
 *  3650 will produce a 400 from the serializer. */
const FORCE_FULL_DAYS_BACK_MAX = 3650
/** Hard timeout (ms) on a single sync request. If we hit this we
 *  drop the in-flight mutex and surface an error so the next click
 *  / next backfill chunk isn't deadlocked behind a request that
 *  never resolves (we've seen this when the backend Celery worker
 *  silently drops a task and the HTTP layer doesn't notice).
 *
 *  120s comfortably covers a worst-case full date-range pull from
 *  Microsoft Graph — typical force_full=true syncs return well
 *  inside 30s even for 365-day windows. */
const SYNC_REQUEST_TIMEOUT_MS = 120_000

/** Fired after a sync that returned a Celery task_id (HTTP 202). The
 *  inbox/stats refetch is debounced by the caller listening on this
 *  event so the actual emails appear once the worker finishes. */
export const EMAIL_SYNC_QUEUED_EVENT = 'email-sync-queued'

function doSync(
  daysBack: number,
  forceFull: boolean = false,
  accountId?: number,
): Promise<SyncResult> {
  if (g_syncInFlight) {
    // Dedup identical-or-weaker requests against the in-flight sync.
    // A force_full caller, however, is asking for a fresh date-range
    // pull — piggybacking it on a delta-mode auto-sync would silently
    // return 0 and the user's "Last 180 days" click would do nothing.
    // In that case wait for the current sync to settle, then fire a
    // proper force_full. (We don't cancel the in-flight request — it's
    // already on the wire — we just chain afterwards.)
    if (!forceFull || g_syncInFlightForceFull) {
      return g_syncInFlight
    }
    return g_syncInFlight.catch(() => undefined).then(() => doSync(daysBack, true, accountId))
  }
  // Backend post-fix accepts up to 3650, but the client-side default
  // for delta-mode syncs remains conservative at 30 — wider sweeps
  // should be opted-in via force_full=true (deep-sync menu's "Force
  // full resync", manual window buttons on the settings page, and
  // every backfill walker chunk).
  const clampedDaysBack = Math.max(
    1,
    Math.min(daysBack, forceFull ? FORCE_FULL_DAYS_BACK_MAX : SERVER_DAYS_BACK_MAX),
  )
  setSyncIsSyncing(true)
  setSyncLastError(null)
  setSyncLastResult(null)
  const promise = (async (): Promise<SyncResult> => {
    try {
      // Race the actual request against a hard timeout. Without
      // this, a request that hangs forever (network drop while a
      // proxy is buffering, backend Celery worker silently dropping
      // a task, etc) would hold `g_syncInFlight` indefinitely and
      // every subsequent click / backfill chunk would be coalesced
      // into the doomed promise. Hitting the timeout throws a
      // synthetic "Sync timed out" error which falls through to the
      // existing `finally` and clears the mutex so the next caller
      // gets a fresh attempt.
      const requestPromise = emailApi<SyncEndpointResponse>('/api/emails/sync/', {
        method: 'POST',
        body: JSON.stringify({
          days_back: clampedDaysBack,
          // `force_full=true` makes Quotech-backend ignore its stored
          // Microsoft Graph delta_token and do a fresh date-range
          // pull. We need this when the delta cursor has gone stale
          // (Graph silently returns "no changes" while the mailbox
          // actually has new mail) — symptom: Inbox locks at an old
          // date despite Outlook showing connected.
          force_full: forceFull,
          ...(accountId != null ? { account_id: accountId } : {}),
        }),
      })
      let timeoutId: number | null = null
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(new Error(`Sync timed out after ${Math.round(SYNC_REQUEST_TIMEOUT_MS / 1000)}s`))
        }, SYNC_REQUEST_TIMEOUT_MS)
      })
      const data = await Promise.race([requestPromise, timeoutPromise])
        .finally(() => {
          if (timeoutId !== null) {
            window.clearTimeout(timeoutId)
            timeoutId = null
          }
        })
      // The Quotech backend dispatches sync to Celery and returns 202
      // with a `task_id` BUT NO emails_fetched/emails_created fields.
      // If we naively read those as zero we'd report "fetched 0,
      // reconnect Outlook" every single sync — even when the worker
      // is happily pulling thousands of messages in the background.
      //
      // The discriminator is the ABSENCE of counts, not the presence of a
      // task_id string. BUG-2026-07-26-EMAIL-SYNC-ALREADY-QUEUED-READ-AS-
      // ZERO-FETCH: the BE gained a suppressed-enqueue ack that carries
      // `status: 'already_queued'` with `task_id: null`, which failed the
      // old task_id check, fell through to the synchronous branch, and
      // painted a "received 0 emails - reconnect Outlook" warning at a user
      // whose mailbox was syncing normally. Only a response that actually
      // reports counts may be read as a completed sync.
      const looksQueued =
        data?.emails_fetched === undefined &&
        data?.emails_created === undefined
      if (looksQueued) {
        // Suppressed enqueue: a sync for this mailbox is already queued or
        // running, so ours was intentionally not stacked. Still a queued
        // outcome (emails are on their way), but callers phrase it
        // differently — "already running" beats "started".
        const alreadyPending =
          data?.status === 'already_queued' ||
          data?.code === 'sync_already_pending'
        const queuedResult: SyncResult = {
          success: true,
          emails_fetched: 0,
          emails_created: 0,
          duration: 0,
          queued: true,
          already_pending: alreadyPending,
          task_id: typeof data?.task_id === 'string' ? data.task_id : undefined,
        }
        setSyncLastResult(queuedResult)
        // Tell anyone listening (EmailLayout, InboxPage) to refetch
        // the inbox in a few seconds once the worker likely finished.
        // Fires for the suppressed case too — the in-flight sync that
        // caused the suppression is exactly what the ladder waits for.
        try {
          window.dispatchEvent(
            new CustomEvent(EMAIL_SYNC_QUEUED_EVENT, {
              detail: {
                task_id: queuedResult.task_id,
                days_back: clampedDaysBack,
                already_pending: alreadyPending,
              },
            }),
          )
        } catch { /* ignore */ }
        return queuedResult
      }
      const syncResult: SyncResult = {
        success: data.success ?? true,
        emails_fetched: data.emails_fetched ?? 0,
        emails_created: data.emails_created ?? 0,
        duration: data.duration_seconds ?? data.duration ?? 0,
      }
      setSyncLastResult(syncResult)
      return syncResult
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed'
      // "Sync already in progress" from the *backend* (cross-tab race,
      // stale server lock) — treat as a soft no-op so the calling
      // loop doesn't log it as a failure. The other instance will
      // refresh the inbox shortly.
      if (looksLikeAlreadyInProgress(msg)) {
        try {
          // eslint-disable-next-line no-console
          console.debug('[useSyncEmails] backend reports sync already running; treating as no-op')
        } catch { /* ignore */ }
        const noop: SyncResult = {
          success: false, emails_fetched: 0, emails_created: 0, duration: 0,
        }
        setSyncLastResult(noop)
        return noop
      }
      // Capacity 429s are handled by useEmailSyncAction (pause + toast).
      // Do not sticky-cache them as syncLastError — auto-sync would paint
      // a permanent failure strip after a transient load spike.
      const isCapacity429 =
        err instanceof QuotechApiError
        && err.status === 429
        && err.code === 'capacity_rejected'
      if (!isCapacity429) {
        setSyncLastError(msg)
      }
      if (looksLikeAuthFailure(err)) {
        try {
          window.dispatchEvent(
            new CustomEvent(EMAIL_SYNC_AUTH_FAILED_EVENT, {
              detail: { message: msg },
            }),
          )
        } catch {
          // ignore — old browsers without CustomEvent ctor
        }
      }
      throw err
    } finally {
      g_syncInFlight = null
      g_syncInFlightForceFull = false
      setSyncIsSyncing(false)
    }
  })()
  g_syncInFlight = promise
  g_syncInFlightForceFull = forceFull
  return promise
}

export function useSyncEmails() {
  const [, forceRender] = useState(0)
  useEffect(() => {
    const cb = () => forceRender(n => n + 1)
    g_syncSubscribers.add(cb)
    return () => {
      g_syncSubscribers.delete(cb)
    }
  }, [])
  const sync = useCallback(
    (daysBack: number = 7, opts?: { forceFull?: boolean }) =>
      doSync(daysBack, !!opts?.forceFull),
    [],
  )
  const syncAccount = useCallback(
    (accountId: number, daysBack: number = 30, opts?: { forceFull?: boolean }) =>
      doSync(daysBack, !!opts?.forceFull, accountId),
    [],
  )
  return {
    sync,
    syncAccount,
    // `effectiveIsSyncing()` unions the in-flight POST flag with the
    // queued-waiting counter so the Sync button reads "Syncing" for
    // the full chunk lifecycle, not just the 1-2s the HTTP request
    // is in flight. Without this, the backfill walker visibly
    // strobes the button between Sync and Syncing as each chunk's
    // POST resolves while the worker is still processing.
    isSyncing: effectiveIsSyncing(),
    result: g_syncLastResult,
    error: g_syncLastError,
  }
}
